import { useState, useEffect } from "react";
import { Plus, Search, Edit2, Trash2, Upload, RefreshCw, TrendingUp, X, Check } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const InstrumentManagement = () => {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(null);
  const [importingBist, setImportingBist] = useState(false);

  useEffect(() => {
    fetchInstruments();
  }, []);

  const fetchInstruments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/instruments?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setInstruments(data);
    } catch (error) {
      console.error("Error fetching instruments:", error);
      toast.error("Failed to load instruments");
    } finally {
      setLoading(false);
    }
  };

  const handleImportBist = async () => {
    try {
      setImportingBist(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/instruments/import-bist`, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Imported ${data.imported} BIST instruments (${data.skipped} already existed)`);
        fetchInstruments();
      } else {
        toast.error(data.detail || "Failed to import BIST instruments");
      }
    } catch (error) {
      console.error("Error importing BIST:", error);
      toast.error("Failed to import BIST instruments");
    } finally {
      setImportingBist(false);
    }
  };

  const handleDeleteInstrument = async (instrumentId) => {
    if (!window.confirm("Are you sure you want to deactivate this instrument?")) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/api/instruments/${instrumentId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        toast.success("Instrument deactivated");
        fetchInstruments();
      } else {
        toast.error("Failed to deactivate instrument");
      }
    } catch (error) {
      console.error("Error deleting instrument:", error);
      toast.error("Failed to deactivate instrument");
    }
  };

  const handleSaveInstrument = async (instrumentData) => {
    try {
      const token = localStorage.getItem("token");
      const isEdit = !!editingInstrument;
      
      const url = isEdit 
        ? `${API_BASE}/api/instruments/${editingInstrument.id}`
        : `${API_BASE}/api/instruments`;
      
      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(instrumentData),
      });
      
      if (response.ok) {
        toast.success(isEdit ? "Instrument updated" : "Instrument created");
        setShowAddModal(false);
        setEditingInstrument(null);
        fetchInstruments();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save instrument");
      }
    } catch (error) {
      console.error("Error saving instrument:", error);
      toast.error("Failed to save instrument");
    }
  };

  // Filter instruments
  const filteredInstruments = instruments.filter((inst) => {
    const matchesSearch = 
      inst.symbol?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inst.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || inst.type === filterType;
    return matchesSearch && matchesType;
  });

  // Get unique types for filter
  const types = [...new Set(instruments.map((i) => i.type))];

  // Count by type
  const typeCounts = instruments.reduce((acc, inst) => {
    acc[inst.type] = (acc[inst.type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Instrument Management</h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage trading instruments - {instruments.length} total instruments
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleImportBist}
            disabled={importingBist}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            data-testid="import-bist-btn"
          >
            {importingBist ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <Upload size={16} />
            )}
            {importingBist ? "Importing..." : "Import BIST"}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            data-testid="add-instrument-btn"
          >
            <Plus size={16} />
            Add Instrument
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Object.entries(typeCounts).map(([type, count]) => (
          <div
            key={type}
            onClick={() => setFilterType(filterType === type ? "all" : type)}
            className={`p-4 rounded-lg border cursor-pointer transition-all ${
              filterType === type
                ? "bg-blue-50 border-blue-300"
                : "bg-white border-gray-200 hover:border-blue-200"
            }`}
          >
            <div className="text-2xl font-bold text-gray-800">{count}</div>
            <div className="text-sm text-gray-500 capitalize">{type}</div>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search instruments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="search-instruments"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          {types.map((type) => (
            <option key={type} value={type}>
              {type.toUpperCase()}
            </option>
          ))}
        </select>
        <button
          onClick={fetchInstruments}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Instruments Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Symbol</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Spread</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Leverage</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                    Loading instruments...
                  </td>
                </tr>
              ) : filteredInstruments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No instruments found
                  </td>
                </tr>
              ) : (
                filteredInstruments.map((inst) => (
                  <tr key={inst.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <TrendingUp size={16} className="text-blue-500" />
                        <span className="font-mono font-semibold text-blue-600">{inst.symbol}</span>
                        {inst.is_virtual && (
                          <span className="px-1.5 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">Virtual</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate" title={inst.name}>
                      {inst.name}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        inst.type === "forex" ? "bg-green-100 text-green-700" :
                        inst.type === "crypto" ? "bg-orange-100 text-orange-700" :
                        inst.type === "stocks" ? "bg-blue-100 text-blue-700" :
                        inst.type === "indices" ? "bg-purple-100 text-purple-700" :
                        inst.type === "commodities" ? "bg-yellow-100 text-yellow-700" :
                        "bg-gray-100 text-gray-700"
                      }`}>
                        {inst.type?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{inst.category || "-"}</td>
                    <td className="px-4 py-3 text-sm font-mono">{inst.spread || 0}</td>
                    <td className="px-4 py-3 text-sm font-mono">1:{inst.leverage_max || 100}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        inst.is_active !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}>
                        {inst.is_active !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setEditingInstrument(inst);
                            setShowAddModal(true);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteInstrument(inst.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Deactivate"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination info */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
          Showing {filteredInstruments.length} of {instruments.length} instruments
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <InstrumentModal
          instrument={editingInstrument}
          onClose={() => {
            setShowAddModal(false);
            setEditingInstrument(null);
          }}
          onSave={handleSaveInstrument}
        />
      )}
    </div>
  );
};

// Instrument Add/Edit Modal
const InstrumentModal = ({ instrument, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    symbol: instrument?.symbol || "",
    name: instrument?.name || "",
    type: instrument?.type || "stocks",
    category: instrument?.category || "",
    exchange: instrument?.exchange || "",
    tradingview_symbol: instrument?.tradingview_symbol || "",
    spread: instrument?.spread || 0.05,
    commission: instrument?.commission || 0.001,
    leverage_max: instrument?.leverage_max || 5,
    min_lot: instrument?.min_lot || 1,
    max_lot: instrument?.max_lot || 10000,
    swap_long: instrument?.swap_long || 0,
    swap_short: instrument?.swap_short || 0,
    is_active: instrument?.is_active !== false,
    is_virtual: instrument?.is_virtual || false,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.symbol || !formData.name) {
      toast.error("Symbol and Name are required");
      return;
    }
    onSave(formData);
  };

  // Cyan color for labels
  const labelClass = "block text-sm font-medium mb-2" + " text-cyan-500";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-8 py-5 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-medium text-cyan-500">
            {instrument ? `Edit Instrument: ${instrument.symbol}` : "Add New Instrument"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ×
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Symbol & Name Row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Symbol</label>
              <input
                type="text"
                value={formData.symbol}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="ALCAR"
                data-testid="instrument-symbol"
              />
            </div>
            <div>
              <label className={labelClass}>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="ALARKO CARRIER SANAYI VE TICARET A.S."
                data-testid="instrument-name"
              />
            </div>
          </div>

          {/* Type & Category Row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="stocks">Stocks</option>
                <option value="forex">Forex</option>
                <option value="crypto">Crypto</option>
                <option value="indices">Indices</option>
                <option value="commodities">Commodities</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Producer Manufacturing"
              />
            </div>
          </div>

          {/* Exchange & Leverage Row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Exchange</label>
              <input
                type="text"
                value={formData.exchange}
                onChange={(e) => setFormData({ ...formData, exchange: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="BIST"
              />
            </div>
            <div>
              <label className={labelClass}>Leverage</label>
              <select
                value={formData.leverage_max}
                onChange={(e) => setFormData({ ...formData, leverage_max: parseInt(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="1">1:1</option>
                <option value="2">1:2</option>
                <option value="5">1:5</option>
                <option value="10">1:10</option>
                <option value="20">1:20</option>
                <option value="50">1:50</option>
                <option value="100">1:100</option>
                <option value="200">1:200</option>
                <option value="500">1:500</option>
              </select>
            </div>
          </div>

          {/* Min Lot & Max Lot Row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Min Lot Size</label>
              <input
                type="number"
                step="0.01"
                value={formData.min_lot}
                onChange={(e) => setFormData({ ...formData, min_lot: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <div>
              <label className={labelClass}>Max Lot Size</label>
              <input
                type="number"
                value={formData.max_lot}
                onChange={(e) => setFormData({ ...formData, max_lot: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

          {/* Spread & Commission Row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Spread</label>
              <input
                type="number"
                step="0.001"
                value={formData.spread}
                onChange={(e) => setFormData({ ...formData, spread: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <div>
              <label className={labelClass}>Commission (%)</label>
              <input
                type="number"
                step="0.0001"
                value={formData.commission}
                onChange={(e) => setFormData({ ...formData, commission: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

          {/* Swap Long & Swap Short Row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Swap Long</label>
              <input
                type="number"
                step="0.01"
                value={formData.swap_long}
                onChange={(e) => setFormData({ ...formData, swap_long: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <div>
              <label className={labelClass}>Swap Short</label>
              <input
                type="number"
                step="0.01"
                value={formData.swap_short}
                onChange={(e) => setFormData({ ...formData, swap_short: parseFloat(e.target.value) })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

          {/* Status Row */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Status</label>
              <select
                value={formData.is_active ? "active" : "inactive"}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.value === "active" })}
                className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div></div>
          </div>

          {/* TradingView Symbol Row */}
          <div>
            <label className={labelClass}>TradingView Symbol</label>
            <input
              type="text"
              value={formData.tradingview_symbol}
              onChange={(e) => setFormData({ ...formData, tradingview_symbol: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded bg-gray-50 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              placeholder="BIST:ALCAR"
            />
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 pt-4"></div>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <button
              type="submit"
              className="flex items-center gap-2 px-6 py-3 bg-cyan-500 text-white rounded hover:bg-cyan-600 font-medium"
              data-testid="save-instrument-btn"
            >
              <Check size={16} />
              Save Changes
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex items-center gap-2 px-6 py-3 bg-red-400 text-white rounded hover:bg-red-500 font-medium"
            >
              <X size={16} />
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InstrumentManagement;
